import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUp, Mail, Hourglass } from "lucide-react";
import { toast } from "sonner";

import { Brand } from "@/components/Brand";
import { startIntake, sendMessage, generateReport, getReportStatus } from "@/lib/api";

const TOTAL_Q = 3;

const LOADING_STEPS = [
  "Analyseren van pijnpunten en kostenstructuur...",
  "Identificeren van organisatie-brede automatisering...",
  "Berekenen van geschatte besparing per use case...",
  "Opmaken van het 90-dagen implementatieplan...",
];

export default function Intake() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [email, setEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [initializing, setInitializing] = useState(true);
  const [initFailed, setInitFailed] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const bootIntake = async () => {
    setInitializing(true);
    setInitFailed(false);
    try {
      const data = await startIntake();
      setSessionId(data.session_id);
      setMessages([{ role: "assistant", text: data.message }]);
      setQuestionCount(1);
    } catch (e) {
      setInitFailed(true);
      toast.error("Kon intake niet starten. Probeer opnieuw.");
    } finally {
      setInitializing(false);
    }
  };

  useEffect(() => {
    bootIntake();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || !sessionId) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const data = await sendMessage(sessionId, text);
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
      setQuestionCount(Math.min(data.question_count, TOTAL_Q));
    } catch {
      toast.error("Er ging iets mis. Probeer het nogmaals.");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleGenerate = async () => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Vul een geldig e-mailadres in.");
      return;
    }
    setGenerating(true);
    setSecondsLeft(60);
    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % LOADING_STEPS.length;
      setGenStep(i);
    }, 1400);
    const countdown = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);

    let pollHandle = null;
    let safetyHandle = null;
    const cleanup = () => {
      clearInterval(interval);
      clearInterval(countdown);
      if (pollHandle) clearInterval(pollHandle);
      if (safetyHandle) clearTimeout(safetyHandle);
    };

    try {
      // 1) Kick off background generation (returns immediately)
      const ack = await generateReport(sessionId, email);

      // If somehow already completed (idempotent re-click), redirect right away
      if (ack.status === "completed") {
        cleanup();
        navigate(`/report/${sessionId}`);
        return;
      }

      // 2) Poll status every 3s
      pollHandle = setInterval(async () => {
        try {
          const s = await getReportStatus(sessionId);
          if (s.status === "completed") {
            cleanup();
            navigate(`/report/${sessionId}`);
          } else if (s.status === "failed") {
            cleanup();
            setGenerating(false);
            const detail = s.error || "Onbekende fout";
            console.error("[generate-report] background failed:", detail);
            const friendly = /JSON/i.test(detail)
              ? "Het rapport kon niet correct worden opgemaakt. Probeer opnieuw — meestal werkt een tweede poging."
              : `Kon rapport niet genereren: ${detail}`;
            toast.error(friendly, { duration: 8000 });
          }
          // else "generating" → keep polling
        } catch (pollErr) {
          // Single poll failure: ignore (retry on next tick)
          console.warn("[poll] status fetch failed, retrying:", pollErr?.message);
        }
      }, 3000);

      // 3) Safety: if 4 minutes pass without completion, give up
      safetyHandle = setTimeout(() => {
        cleanup();
        setGenerating(false);
        toast.error("Het duurt langer dan verwacht. Probeer opnieuw.", { duration: 8000 });
      }, 240000);
    } catch (e) {
      cleanup();
      setGenerating(false);
      const detail =
        e?.response?.data?.detail ||
        e?.response?.statusText ||
        e?.message ||
        "Onbekende fout";
      const status = e?.response?.status;
      console.error("[generate-report] failed:", { status, detail, error: e });
      const friendly =
        status === 500 && /JSON/i.test(detail)
          ? "Het rapport kon niet correct worden opgemaakt. Probeer het opnieuw — meestal werkt een tweede poging."
          : status === 404
          ? "Sessie niet gevonden. Vernieuw de pagina en begin opnieuw."
          : `Kon rapport niet genereren: ${detail}`;
      toast.error(friendly, { duration: 8000 });
    }
  };

  const canGenerate = questionCount >= 2 && messages.length >= 3;
  const progress = Math.min((questionCount / TOTAL_Q) * 100, 100);

  if (initializing || initFailed) {
    return (
      <div className="min-h-screen bg-[#1B1B3A] flex flex-col">
        <div className="border-b border-white/10">
          <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
            <Brand variant="dark" />
            <button
              onClick={() => navigate("/")}
              className="font-condensed uppercase text-xs tracking-[0.25em] text-white/60 hover:text-[#3FCBA6] transition-colors flex items-center gap-2"
              data-testid="init-back-button"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
              Terug
            </button>
          </div>
        </div>
        <div className="flex-1 max-w-[1500px] w-full mx-auto px-6 lg:px-12 py-16 flex flex-col items-center justify-center text-center">
          {!initFailed ? (
            <>
              <div className="relative mb-10" data-testid="intake-initial-loader">
                <Hourglass
                  className="w-24 h-24 text-[#3FCBA6]"
                  strokeWidth={1.4}
                  style={{
                    animation: "hourglass-flip 2.4s cubic-bezier(0.65, 0, 0.35, 1) infinite",
                    transformOrigin: "center",
                  }}
                />
                <div className="absolute inset-0 rounded-full border-2 border-[#3FCBA6]/20 animate-pulse" style={{ margin: "-16px" }} />
              </div>
              <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#3FCBA6] font-semibold mb-5">
                · moment ·
              </div>
              <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl mb-5">
                We starten<br />de <span style={{ fontStyle: "italic" }}>intake</span>...
              </h1>
              <p className="text-white/60 text-base lg:text-lg max-w-md leading-relaxed">
                Een paar seconden en je eerste vraag staat klaar.
              </p>
              <div className="flex items-center gap-2 mt-10" aria-hidden="true">
                <span className="w-2 h-2 bg-[#3FCBA6] animate-pulse" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-[#3FCBA6] animate-pulse" style={{ animationDelay: "200ms" }} />
                <span className="w-2 h-2 bg-[#3FCBA6] animate-pulse" style={{ animationDelay: "400ms" }} />
              </div>
            </>
          ) : (
            <>
              <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#3FCBA6] font-semibold mb-5">
                · iets ging mis ·
              </div>
              <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl mb-5">
                Verbinding<br />mislukt.
              </h1>
              <p className="text-white/60 text-base lg:text-lg max-w-md leading-relaxed mb-8">
                We konden de intake niet starten. Probeer het nog eens.
              </p>
              <button
                onClick={bootIntake}
                className="tp-btn tp-btn--mint"
                data-testid="init-retry-button"
              >
                Opnieuw proberen
              </button>
            </>
          )}
        </div>
        <style>{`
          @keyframes hourglass-flip {
            0% { transform: rotate(0deg); }
            45% { transform: rotate(0deg); }
            55% { transform: rotate(180deg); }
            100% { transform: rotate(180deg); }
          }
        `}</style>
      </div>
    );
  }

  if (generating) {
    return (
      <div className="min-h-screen bg-[#1B1B3A] flex flex-col">
        <div className="border-b border-white/10">
          <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
            <Brand variant="dark" />
            <div className="font-condensed uppercase text-xs tracking-[0.25em] text-[#3FCBA6] flex items-center gap-2">
              <span className="w-2 h-2 bg-[#3FCBA6] animate-pulse" />
              Genereren
            </div>
          </div>
        </div>
        <div className="flex-1 max-w-[1500px] w-full mx-auto px-6 lg:px-12 py-16 grid grid-cols-12 gap-8 items-center">
          {/* LEFT — text + steps */}
          <div className="col-span-12 lg:col-span-8">
            <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#3FCBA6] font-semibold mb-6">
              · stap {genStep + 1} van {LOADING_STEPS.length} · ca. 60 seconden ·
            </div>
            <h1 className="font-display text-white text-5xl lg:text-7xl mb-6">
              Ik bouw je<br /><span className="italic-em" style={{ fontStyle: "italic" }}>AI Business Case</span>.
            </h1>
            <p className="text-white/60 text-base lg:text-lg mb-10 max-w-xl leading-relaxed">
              Even geduld — ik analyseer jouw antwoorden, bereken de jaarlijkse impact per use case
              en maak het 90-dagen plan op. Dit duurt <span className="text-[#3FCBA6] font-semibold">ongeveer 60 seconden</span>.
            </p>

            <div className="space-y-3 max-w-2xl">
              {LOADING_STEPS.map((step, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-5 py-4 px-6 border-2 transition-all ${
                    i < genStep ? "border-white/10 text-white/40" :
                    i === genStep ? "border-[#3FCBA6] bg-[#3FCBA6] text-[#1B1B3A]" :
                    "border-white/15 text-white/40"
                  }`}
                  data-testid={i === genStep ? "generating-step-text" : undefined}
                >
                  <span className="font-display text-2xl">{String(i + 1).padStart(2, "0")}</span>
                  {i === genStep && <span className="w-2 h-2 bg-[#1B1B3A] animate-pulse" />}
                  <span className="font-medium flex-1">{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Hourglass + countdown */}
          <div className="hidden lg:flex col-span-4 flex-col items-center justify-center gap-8">
            <div className="relative">
              {/* Animated hourglass */}
              <Hourglass
                className="w-40 h-40 text-[#3FCBA6]"
                strokeWidth={1.2}
                style={{
                  animation: "hourglass-flip 2.4s cubic-bezier(0.65, 0, 0.35, 1) infinite",
                  transformOrigin: "center",
                }}
              />
              {/* Sand-like glow ring */}
              <div className="absolute inset-0 rounded-full border-2 border-[#3FCBA6]/20 animate-pulse" style={{margin: "-20px"}} />
            </div>
            <div className="text-center">
              <div className="font-display text-7xl text-white" data-testid="generating-countdown">
                {secondsLeft.toString().padStart(2, "0")}<span className="text-[#3FCBA6]">s</span>
              </div>
              <div className="font-condensed uppercase text-xs tracking-[0.25em] text-white/40 font-semibold mt-3">
                {secondsLeft > 0 ? "geschatte tijd" : "bijna klaar..."}
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes hourglass-flip {
            0% { transform: rotate(0deg); }
            45% { transform: rotate(0deg); }
            55% { transform: rotate(180deg); }
            100% { transform: rotate(180deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Top bar + progress */}
      <header className="border-b-2 border-[#1B1B3A] bg-white sticky top-0 z-20">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 font-condensed uppercase text-sm tracking-[0.15em] font-semibold text-[#1B1B3A] hover:text-[#3FCBA6] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            Terug
          </button>
          <Brand />
          <div className="font-display text-2xl text-[#1B1B3A] flex items-center gap-2" data-testid="progress-counter">
            <span className="text-[#3FCBA6]">{Math.min(questionCount, TOTAL_Q).toString().padStart(2, "0")}</span>
            <span className="text-[#1B1B3A]/30">/</span>
            <span>{TOTAL_Q.toString().padStart(2, "0")}</span>
          </div>
        </div>
        <div className="h-[3px] bg-[#E8E8EE]">
          <motion.div
            className="h-full bg-[#3FCBA6]"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            data-testid="progress-bar"
          />
        </div>
      </header>

      {/* Chat */}
      <main className="flex-1 grid grid-cols-12">
        {/* Left sidebar: question tracker */}
        <div className="hidden lg:block col-span-3 border-r-2 border-[#1B1B3A] bg-[#F8F8FA] relative">
          <div className="sticky top-24 px-8 py-12">
            <div className="font-condensed uppercase tracking-[0.25em] text-xs font-semibold text-[#6B6B85] mb-6">
              · jouw 3 vragen ·
            </div>
            <div className="space-y-1">
              {Array.from({ length: TOTAL_Q }).map((_, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 py-2.5 transition-colors ${
                    i < questionCount ? "text-[#1B1B3A]" : "text-[#1B1B3A]/30"
                  }`}
                >
                  <span className={`font-display text-3xl ${i < questionCount ? "text-[#3FCBA6]" : "text-[#1B1B3A]/20"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-condensed uppercase text-xs tracking-[0.15em] font-semibold">
                    Vraag {i + 1}
                  </span>
                  {i < questionCount && <span className="ml-auto w-2 h-2 bg-[#3FCBA6]" />}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-9 flex flex-col px-6 lg:px-16 py-10">
          <div
            ref={scrollRef}
            className="chat-scroll flex-1 overflow-y-auto space-y-6 pr-2 pb-4 max-h-[calc(100vh-280px)]"
            data-testid="chat-container"
          >
            <AnimatePresence initial={false}>
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className={
                    m.role === "assistant"
                      ? "flex items-start gap-4"
                      : "flex items-start gap-4 justify-end"
                  }
                  data-testid={`message-${m.role}`}
                >
                  {m.role === "assistant" && (
                    <div className="w-10 h-10 bg-[#1B1B3A] flex items-center justify-center flex-shrink-0 font-display text-[#3FCBA6] text-lg">
                      AI
                    </div>
                  )}
                  <div
                    className={
                      m.role === "assistant"
                        ? "bg-[#F8F8FA] border-2 border-[#1B1B3A] px-6 py-5 max-w-[85%] text-[#1B1B3A] leading-relaxed whitespace-pre-wrap"
                        : "bg-[#3FCBA6] text-[#1B1B3A] px-6 py-5 max-w-[85%] leading-relaxed whitespace-pre-wrap font-medium"
                    }
                  >
                    {m.text}
                  </div>
                </motion.div>
              ))}

              {sending && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-start gap-4"
                  data-testid="typing-indicator"
                >
                  <div className="w-10 h-10 bg-[#1B1B3A] flex items-center justify-center flex-shrink-0 font-display text-[#3FCBA6] text-lg">
                    AI
                  </div>
                  <div className="bg-[#F8F8FA] border-2 border-[#1B1B3A] px-6 py-5">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Generate report affordance */}
          {canGenerate && !showEmailGate && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3"
            >
              <button
                onClick={() => setShowEmailGate(true)}
                className="tp-btn tp-btn--mint w-full justify-between text-base py-5"
                data-testid="open-generate-report-button"
              >
                <span>Genereer mijn rapport</span>
              </button>
              <p className="font-condensed uppercase text-xs tracking-[0.2em] text-[#6B6B85] text-center mt-3">
                Tip: hoe meer je deelt, hoe concreter je plan
              </p>
            </motion.div>
          )}

          {/* Input or email gate */}
          {showEmailGate ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-2 border-[#1B1B3A] bg-white p-8"
            >
              <div className="flex items-center gap-2 mb-4 font-condensed uppercase text-xs tracking-[0.25em] font-semibold text-[#3FCBA6]">
                <Mail className="w-3.5 h-3.5" strokeWidth={2.5} />
                · Laatste stap ·
              </div>
              <h3 className="font-display text-3xl lg:text-5xl text-[#1B1B3A] mb-3">
                Waar mogen we je<br />rapport heen sturen?
              </h3>
              <p className="text-[#1B1B3A]/70 mb-6">
                Direct beschikbaar als PDF. Geen spam, beloofd.
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jij@jouwwerk.nl"
                className="w-full bg-white border-2 border-[#1B1B3A] px-5 py-4 text-lg text-[#1B1B3A] focus:outline-none focus:bg-[#3FCBA6]/10 transition-colors font-medium"
                data-testid="email-input"
                autoFocus
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setShowEmailGate(false)}
                  className="tp-btn--ghost px-6 py-3 font-condensed uppercase text-sm tracking-[0.15em] font-semibold border-2 border-[#1B1B3A]"
                  data-testid="cancel-email-button"
                >
                  ← Terug
                </button>
                <button onClick={handleGenerate} className="tp-btn tp-btn--mint flex-1 justify-center" data-testid="submit-generate-button">
                  Maak mijn actieplan
                </button>
              </div>
            </motion.div>
          ) : (
            <form
              onSubmit={handleSend}
              className="border-2 border-[#1B1B3A] bg-white p-3 flex items-end gap-3 focus-within:bg-[#3FCBA6]/5 transition-colors"
            >
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                rows={1}
                placeholder='Typ je antwoord (zeg "sla over" om over te slaan)...'
                className="flex-1 resize-none bg-transparent border-none outline-none text-[#1B1B3A] text-base py-3 px-3 min-h-[48px] max-h-36 font-body"
                data-testid="message-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                className="bg-[#1B1B3A] hover:bg-[#3FCBA6] hover:text-[#1B1B3A] disabled:bg-[#E8E8EE] disabled:cursor-not-allowed text-white w-12 h-12 flex items-center justify-center transition-colors duration-200 flex-shrink-0"
                data-testid="send-message-button"
              >
                <ArrowUp className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
